const TIMEZONE = 'Europe/Amsterdam';

const WEEKDAY_TO_KEY = {
  monday: 'monday_hours',
  tuesday: 'tuesday_hours',
  wednesday: 'wednesday_hours',
  thursday: 'thursday_hours',
  friday: 'friday_hours',
  saturday: 'saturday_hours',
  sunday: 'sunday_hours',
};

const CLOSED_TOKENS = ['gesloten', 'closed', 'geschlossen', 'fermé', 'ferme'];
const APPOINTMENT_TOKENS = [
  'op afspraak',
  'by appointment',
  'nach vereinbarung',
  'sur rendez-vous',
  'sur rendezvous',
];

// "Binnenkort open" is shown this many minutes before a store opens.
const OPENING_SOON_MINUTES = 90;

function getAmsterdamNow() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TIMEZONE,
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());

  const get = (type) => parts.find((part) => part.type === type)?.value;
  const hour = Number(get('hour'));
  const minute = Number(get('minute'));

  return {
    weekday: get('weekday').toLowerCase(),
    minutesNow: hour * 60 + minute,
  };
}

function timeToMinutes(time) {
  const [hours, minutes = 0] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

function detectStatus(value) {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (CLOSED_TOKENS.includes(normalized)) return 'closed';
  if (APPOINTMENT_TOKENS.includes(normalized)) return 'appointment';
  return 'open';
}

function parseRanges(value) {
  if (!value || typeof value !== 'string') return null;

  const status = detectStatus(value);
  if (status === 'closed') return 'closed';
  if (status === 'appointment') return 'op afspraak';

  return value
    .split(/\s*,\s*|\s+en\s+/i)
    .map((range) => {
      const [start, end] = range.split(' - ').map((part) => part.trim());
      if (!start || !end) return null;

      return {
        startMinutes: timeToMinutes(start),
        endMinutes: timeToMinutes(end),
      };
    })
    .filter(Boolean);
}

function getStatusFromHours(hoursData, labels) {
  const now = getAmsterdamNow();
  const hoursKey = WEEKDAY_TO_KEY[now.weekday];
  const value = String(hoursData?.[hoursKey] ?? '').trim();

  if (!value) {
    return { modifier: 'closed', label: labels.closed };
  }

  const ranges = parseRanges(value);

  if (ranges === 'closed') {
    return { modifier: 'closed', label: labels.closed };
  }

  if (ranges === 'op afspraak') {
    return { modifier: 'appointment', label: labels.by_appointment };
  }

  for (const range of ranges || []) {
    if (now.minutesNow >= range.startMinutes && now.minutesNow <= range.endMinutes) {
      return { modifier: 'open', label: labels.open };
    }
  }

  for (const range of ranges || []) {
    const minutesUntilOpen = range.startMinutes - now.minutesNow;
    if (minutesUntilOpen > 0 && minutesUntilOpen <= OPENING_SOON_MINUTES) {
      return { modifier: 'opening-soon', label: labels.opening_soon };
    }
  }

  return { modifier: 'closed', label: labels.closed };
}

class AudicienLocatieBadge extends HTMLElement {
  connectedCallback() {
    this.#updateStatus();
  }

  #updateStatus() {
    const section = this.closest('.audicien-locatie');
    const labelsElement = section?.querySelector('[data-audicien-locatie-labels]');

    if (!labelsElement) return;

    let labels;
    let hoursData;

    try {
      labels = JSON.parse(labelsElement.textContent);
      hoursData = JSON.parse(this.dataset.hours || '{}');
    } catch {
      return;
    }

    const { modifier, label } = getStatusFromHours(hoursData, labels);

    this.textContent = label;
    this.classList.remove(
      'audicien-locatie__badge--open',
      'audicien-locatie__badge--closed',
      'audicien-locatie__badge--appointment',
      'audicien-locatie__badge--opening-soon'
    );
    this.classList.add(`audicien-locatie__badge--${modifier}`);
  }
}

if (!customElements.get('audicien-locatie-badge')) {
  customElements.define('audicien-locatie-badge', AudicienLocatieBadge);
}
